import { IntlShape } from "react-intl";
import {
  JsonSchemaProperty,
  JsonSchemaItemProperty,
  RowData,
  SectionValue,
  RowDataReducerState,
  RowDataReducerAction,
  JsonSchema,
  GenericSchemaSection,
  RowDataValueType,
  TabbedSection,
  EvaluatedSchemaContext,
  SetupMode,
  JsonSchemaPropertyMap,
  SectionInfo,
  RowDataObject,
  RecordPageRenderContext,
  LandingPageOptions,
  SetupListNavInfoByDepartment,
} from "../../../types";
import {
  ADDITIONAL_BOTTOM_SECTIONS,
  ADDITIONAL_FIELDS_FOR_SECTION,
  ADDITIONAL_TOP_SECTIONS,
} from "../components/custom-renderers/constants";
import {
  COA_EXPORT_READ_ONLY_FIELDS,
  DASHBOARD_LEVEL_SETUPS,
  DEPARTMENTS,
  NORMAL_TAB_SETUPS,
  PRIVLIB_SETUPS,
  ROW_DATA_KEYS,
  SCHEMA_SECTIONS,
  SCHEMA_TITLES,
  SECTION_TITLE_OVERRIDES,
  SETUP_DESCRIPTIONS,
  SIDE_SHEET_SETUPS,
  SINGLE_FIELD_DASHBOARD_LEVEL_SETUPS,
} from "../constants";
import { isNewRecord } from "./rowDataHelpers";
import { collectFields } from "./tableHelpers";
import { TFunction } from "i18next";
import { getHeaderSetupsList } from "./setups-dashboard";

export const getTitleFromTargetPropertyKey = ({
  targetKey,
  sourceKey,
  rowIndex,
  rowDataReducerState,
}: {
  targetKey: string;
  sourceKey: string | undefined;
  rowIndex: number | undefined;
  rowDataReducerState: RowDataReducerState;
}): string => {
  const schemaProperties = rowDataReducerState.schema.properties;

  if (rowIndex !== undefined && sourceKey !== undefined) {
    return (
      schemaProperties[sourceKey].items?.properties?.[targetKey]?.title ?? ""
    );
  } else if (sourceKey && rowIndex === undefined) {
    const nestedProperty = schemaProperties[sourceKey]?.properties;
    return nestedProperty
      ? nestedProperty[targetKey].title
      : schemaProperties[targetKey].title;
  } else {
    return schemaProperties[targetKey].title;
  }
};

export const getInputType = (typeField: string[] | string): string => {
  const validTypes = new Set([
    "string",
    "number",
    "integer",
    "boolean",
    "array",
    "object",
    "button", // for customized buttons, and which are not part of the schema
    "etc-absense-code-classification-ref-component", // for customized setup specific component
    "discount-copy-settings-component", // for discount copy settings custom component
  ]);

  if (Array.isArray(typeField)) {
    const foundType = typeField.find((type) => validTypes.has(type));
    return foundType ?? "string";
  } else if (validTypes.has(typeField)) {
    return typeField;
  }

  return "string";
};

export const getIsDisabled = ({
  property,
  rowData,
  rowIndex,
  isReadOnly,
}: {
  property: JsonSchemaProperty | JsonSchemaItemProperty;
  rowData: RowData;
  rowIndex?: number;
  isReadOnly?: boolean;
}): boolean => {
  if (!property || isReadOnly) {
    return true;
  }
  if (rowIndex !== undefined) {
    if (property.disabled) {
      return property.disabled === true;
    }
  }
  if (property?.disabled) {
    return property.disabled === true;
  } else if (!property.isEditable && !isNewRecord(rowData)) {
    return property.isEditable === false;
  }
  return false;
};

export const getDefaultSchemaPropertyValue = (
  propertyKey: string,
  rowIndex: number | undefined,
  propertySubKey: string,
  rowDataReducerState: RowDataReducerState,
): RowDataValueType => {
  const { schema, rowData } = rowDataReducerState;
  if (rowIndex !== undefined) {
    return (
      schema.properties[propertyKey]?.items?.properties?.[propertySubKey]
        ?.default ?? null
    );
  } else {
    if (Array.isArray(rowData[propertySubKey])) {
      return schema.properties[propertySubKey]?.default ?? [];
    }
    return schema.properties[propertySubKey]?.default ?? null;
  }
};

export const dispatchUpdateSchemaPropertyField = (
  dispatch: React.Dispatch<RowDataReducerAction>,
  propertyField: string,
  propertyKey: string,
  rowIndex: number | undefined,
  propertySubKey: string,
  newFieldValue: Array<string | null> | boolean | string,
): void => {
  if (rowIndex !== undefined) {
    dispatch({
      type: "UPDATE_SCHEMA_PROPERTY_FIELD",
      payload: {
        propertyKey,
        propertyField,
        newPropertyFieldValue: newFieldValue,
        rowIndex,
        propertySubKey,
      },
    });
  } else {
    dispatch({
      type: "UPDATE_SCHEMA_PROPERTY_FIELD",
      payload: {
        propertyKey: propertySubKey,
        propertyField,
        newPropertyFieldValue: newFieldValue,
      },
    });
  }
};

export const getTabbedSectionKeys = (
  sections: Record<string, SectionValue>,
): string[] => {
  return Object.entries(sections)
    .filter(([, value]) => !Array.isArray(value))
    .map(([key]) => key);
};

export const getFieldsByTabKey = (
  sections: Record<string, SectionValue>,
  tabKey: string | undefined,
): string[] => {
  if (!tabKey || !sections[tabKey]) return [];

  const tabSection = sections[tabKey];

  return collectFields(tabSection);
};

export const getTitleFromKey = (
  key: string,
  t?: TFunction,
  language?: string,
): string => {
  const getDefaultTitle = () => {
    if (SECTION_TITLE_OVERRIDES[key]) return SECTION_TITLE_OVERRIDES[key];

    return key
      .replace(/_fields$/, "")
      .replace(/_/g, " ")
      .replace(/\band\b/g, "&")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  if (language?.startsWith("en")) {
    return getDefaultTitle();
  }

  if (t) {
    const translated = t(`sectionTitles.${key}`);
    if (translated && translated !== `sectionTitles.${key}`) {
      return translated;
    }
  }

  return getDefaultTitle();
};

export const getSchemaSections = (
  schema: JsonSchema,
  selectedDashboardTabSectionKey?: string,
): GenericSchemaSection[] => {
  const setupMode = determineSetupMode(schema);

  const sections = selectedDashboardTabSectionKey
    ? schema.sections[selectedDashboardTabSectionKey]
    : schema.sections;

  if (!sections) return [];

  if (
    setupMode === "NORMAL_TAB_SETUP" ||
    setupMode === "INVENTORY_BUILDINGS_SETUP"
  ) {
    return Object.values(sections).flatMap((tab) => {
      if (!tab || typeof tab !== "object") return [];

      return Object.entries(tab).map(([sectionKey, sectionValue]) => ({
        sectionKey,
        sectionValue: Array.isArray(sectionValue) ? sectionValue : [],
        explicitSectionValueWithProperty: {},
      }));
    });
  }

  return Object.entries(sections)
    .filter(
      ([key, value]) =>
        Array.isArray(value) && key !== SCHEMA_SECTIONS.GRID_FIELDS,
    )
    .map(([key, value]) => ({
      sectionKey: key,
      sectionValue: (value as string[]) || [],
      explicitSectionValueWithProperty: {},
    }));
};

export const isSectionVisible = (
  sectionValue: string[],
  schema: JsonSchema,
): boolean => {
  return sectionValue.some((propertyKey) => {
    const property = schema.properties[propertyKey];

    if (!property) return true; // if the property doesn't exist, assume it's visible as additional fields are allowed
    return property && !property.isHidden;
  });
};

export const getSchemaTitles = (schema: JsonSchema): Record<string, string> => {
  return Object.fromEntries(
    Object.entries(schema.properties).map(([key, value]) => [key, value.title]),
  );
};

export const getFieldsWithUITitles = (
  record: Record<string, unknown>,
  schemaTitles: Record<string, string>,
): Record<string, string> => {
  return Object.fromEntries(
    Object.entries(record)
      .filter(
        ([key, value]) =>
          key in schemaTitles &&
          value !== undefined &&
          value !== null &&
          (typeof value === "string" ||
            typeof value === "number" ||
            typeof value === "boolean"),
      )
      .map(([key, value]) => [schemaTitles[key], String(value)]),
  );
};

export const evaluateSchemaContext = ({
  schema,
  activeTabIndex,
  selectedDashboardTabSectionKey,
  isRecordOpen,
}: {
  schema: JsonSchema;
  activeTabIndex: number;
  selectedDashboardTabSectionKey?: string;
  isRecordOpen: boolean;
}): EvaluatedSchemaContext => {
  const setupMode = determineSetupMode(schema);

  const tabKeys = getTabbedSectionKeys(schema.sections);
  const activeTabKey = tabKeys[activeTabIndex];

  const sections = getSchemaSections(schema, selectedDashboardTabSectionKey);

  const effectiveGridFields = resolveEffectiveGridFields({
    schema,
    setupMode,
    activeTabKey,
  });

  /**
   * Setup table is rendered only when:
   * - dashboard-level setup (tabbed or not)
   * - record is NOT open
   */
  const shouldRenderSetupTable =
    !!effectiveGridFields &&
    (setupMode === "DASHBOARD_LEVEL_SETUP" ||
      setupMode === "DASHBOARD_TABBED_SETUP") &&
    !isRecordOpen;

  return {
    sections,
    tabKeys,
    activeTabKey,
    setupMode,
    effectiveGridFields,
    shouldRenderSetupTable,
  };
};

export const getBaseGridFields = (schema: JsonSchema): Set<string> => {
  return new Set(schema.sections[SCHEMA_SECTIONS.GRID_FIELDS] as string[]);
};

export const determineSetupMode = (schema: JsonSchema): SetupMode => {
  const baseGridFields = getBaseGridFields(schema);
  const setupName = schema.title;

  const hasBaseGridFields = baseGridFields.size > 0;

  if (SIDE_SHEET_SETUPS.includes(setupName)) {
    return "NORMAL_SIDE_SHEET_SETUP";
  }

  if (NORMAL_TAB_SETUPS.includes(setupName)) {
    return "NORMAL_TAB_SETUP";
  }

  if (SCHEMA_TITLES.INVENTORY_BUILDINGS.includes(setupName)) {
    return "INVENTORY_BUILDINGS_SETUP";
  }

  // Base grid fields + single section
  if (DASHBOARD_LEVEL_SETUPS.includes(setupName)) {
    return "DASHBOARD_LEVEL_SETUP";
  }

  // single field can be shown directly on dashboard
  if (SINGLE_FIELD_DASHBOARD_LEVEL_SETUPS.includes(setupName)) {
    return "SINGLE_FIELD_DASHBOARD_LEVEL_SETUP";
  }

  // privlib setups can directly show the first record
  if (PRIVLIB_SETUPS.includes(setupName)) {
    return "SINGLE_RECORD_DASHBOARD_LEVEL_SETUP";
  }

  // No base grid fields → dashboard-level tabbed setup
  if (!hasBaseGridFields) {
    return "DASHBOARD_TABBED_SETUP";
  }

  // Base grid fields + multiple sections
  return "NORMAL_SETUP";
};

export const resolveEffectiveGridFields = ({
  schema,
  setupMode,
  activeTabKey,
}: {
  schema: JsonSchema;
  setupMode: SetupMode;
  activeTabKey?: string;
}): Set<string> | undefined => {
  /**
   * Base-level grid fields (dashboard scope)
   */
  const baseGridFields = getBaseGridFields(schema);
  const hasBaseGridFields = baseGridFields.size > 0;

  /**
   * 1. DASHBOARD_LEVEL_SETUP
   *    → always use base-level grid fields
   */
  if (setupMode === "DASHBOARD_LEVEL_SETUP") {
    return hasBaseGridFields ? baseGridFields : undefined;
  }

  /**
   * 2. NORMAL_SETUP
   *    → base-level grid fields apply globally
   */
  if (setupMode === "NORMAL_SETUP") {
    return hasBaseGridFields ? baseGridFields : undefined;
  }

  /**
   * 3. DASHBOARD_TABBED_SETUP
   *    → grid fields are defined per tab (optional)
   */
  if (setupMode === "DASHBOARD_TABBED_SETUP" && activeTabKey) {
    const tabSection = schema.sections[activeTabKey] as
      | TabbedSection
      | undefined;

    const tabGridFields = tabSection?.[SCHEMA_SECTIONS.GRID_FIELDS];

    return Array.isArray(tabGridFields) && tabGridFields.length > 0
      ? new Set(tabGridFields)
      : undefined;
  }

  return undefined;
};

export const getAdditionalFieldsForSection = ({
  setupKey,
  sectionKey,
}: {
  setupKey: string;
  sectionKey: string;
}): JsonSchemaPropertyMap | undefined => {
  return ADDITIONAL_FIELDS_FOR_SECTION[setupKey]?.[sectionKey];
};

export const getAdditionalSections = ({
  setupName,
  position,
}: {
  setupName: string;
  position: "top" | "bottom";
}): GenericSchemaSection[] | undefined => {
  const sections =
    position === "top"
      ? ADDITIONAL_TOP_SECTIONS[setupName]
      : ADDITIONAL_BOTTOM_SECTIONS[setupName];

  if (!sections) return undefined;

  return Object.entries(sections).map(([sectionKey, sectionValue]) => ({
    sectionKey,
    sectionValue: Object.keys(sectionValue),
    explicitSectionValueWithProperty: sectionValue,
  }));
};

export const getSetupInformation = ({
  selectedDashboardTabSectionKey,
  selectedSetupLabel,
  schemaDescription,
  t,
  language,
}: {
  selectedDashboardTabSectionKey: string | undefined;
  selectedSetupLabel: string;
  schemaDescription: string;
  t: TFunction;
  language: string;
}): { title: string; description: string } => {
  if (selectedDashboardTabSectionKey) {
    return {
      title: getTitleFromKey(selectedDashboardTabSectionKey, t, language),
      description:
        SETUP_DESCRIPTIONS.get(selectedDashboardTabSectionKey) ??
        schemaDescription,
    };
  }

  return {
    title: selectedSetupLabel,
    description: schemaDescription,
  };
};

export const getSectionTooltipInfo = ({
  selectedTabSectionKey,
  selectedDashboardTabSectionKey,
  sectionKey,
  schema,
}: {
  selectedTabSectionKey?: string;
  selectedDashboardTabSectionKey?: string;
  sectionKey: string;
  schema: JsonSchema;
}): string => {
  const DEFAULT_INFO = "Section Information to be added.";

  const { sectionsInfo } = schema;

  if (!sectionsInfo) {
    return DEFAULT_INFO;
  }

  const activeTabKey = selectedDashboardTabSectionKey ?? selectedTabSectionKey;

  if (activeTabKey && typeof sectionsInfo[activeTabKey] === "object") {
    return sectionsInfo[activeTabKey]?.[sectionKey] ?? DEFAULT_INFO;
  }
  return (sectionsInfo as SectionInfo)[sectionKey] ?? DEFAULT_INFO;
};

export const getPageHeaderTitle = ({
  rowDataReducerState,
  isRecordOpen,
  intl,
  isSetupSelected,
  selected,
  setupListNavInfoByDepartment,
}: {
  rowDataReducerState: RowDataReducerState;
  isRecordOpen: boolean;
  isSetupSelected: boolean;
  selected: LandingPageOptions;
  setupListNavInfoByDepartment: SetupListNavInfoByDepartment | undefined;
  intl: IntlShape;
}): string => {
  const { rowData, isReadOnly } = rowDataReducerState;
  const selectedId = getSelectedId(selected);

  if (!isRecordOpen) {
    if (isSetupSelected && setupListNavInfoByDepartment) {
      const headerSetups = getHeaderSetupsList({
        selected,
        setupListNavInfoByDepartment,
      });

      return headerSetups?.subGroup ?? selected.setup.label;
    }
    return intl.formatMessage({
      id: "cdcm.header.home",
      defaultMessage: "Setups",
    });
  }

  if (isNewRecord(rowData)) {
    return intl.formatMessage(
      {
        id: "cdcm.header.new",
        defaultMessage: "{id} | {setup} | New Entry",
      },
      { id: selectedId, setup: selected.setup.label },
    );
  }

  return isReadOnly
    ? intl.formatMessage(
        {
          id: "cdcm.header.view",
          defaultMessage: "{id} | {setup} | View Entry",
        },
        { id: selectedId, setup: selected.setup.label },
      )
    : intl.formatMessage(
        {
          id: "cdcm.header.edit",
          defaultMessage: "{id} | {setup} | Edit Entry",
        },
        { id: selectedId, setup: selected.setup.label },
      );
};

export const getSetupBasedNewEntryText = ({
  setupName,
  intl,
}: {
  setupName: string;
  intl: IntlShape;
}): string => {
  if (SCHEMA_TITLES.INVENTORY_BUILDINGS === setupName)
    return intl.formatMessage({
      id: "button.building",
      defaultMessage: "Building",
    });

  return intl.formatMessage({
    id: "button.newEntry",
    defaultMessage: "New Entry",
  });
};

export const getSideSheetTitle = ({
  rowData,
  selected,
  isReadOnly,
  intl,
}: {
  rowData: RowData;
  selected: LandingPageOptions;
  isReadOnly: boolean | undefined;
  intl: IntlShape;
}): string => {
  const customTitle = getSetupBasedSideSheetTitle({
    setupName: selected.setup.value,
    rowData,
    intl,
    isReadOnly: isReadOnly ?? false,
  });
  if (customTitle) {
    return customTitle;
  }

  // Calculate the ID (Store/Group/Dept) to satisfy translation requirements
  const selectedId = getSelectedId(selected);

  if (isNewRecord(rowData)) {
    return intl.formatMessage(
      {
        id: "cdcm.header.new",
        // Updating defaultMessage to be consistent with getPageHeaderTitle
        // and translation bundle expectations
        defaultMessage: "{id} | {setup} | New Entry",
      },
      { id: selectedId, setup: selected.setup.label },
    );
  }

  return isReadOnly
    ? intl.formatMessage(
        {
          id: "cdcm.header.view",
          defaultMessage: "{id} | {setup} | View Entry",
        },
        { id: selectedId, setup: selected.setup.label },
      )
    : intl.formatMessage(
        {
          id: "cdcm.header.edit",
          defaultMessage: "{id} | {setup} | Edit Entry",
        },
        { id: selectedId, setup: selected.setup.label },
      );
};

export const getSetupBasedSideSheetTitle = ({
  setupName,
  rowData,
  intl,
  isReadOnly,
}: {
  setupName: string;
  rowData: RowData;
  isReadOnly: boolean;
  intl: IntlShape;
}): string | undefined => {
  if (SCHEMA_TITLES.INVENTORY_BUILDINGS === setupName) {
    if (isNewRecord(rowData)) {
      return intl.formatMessage({
        id: "button.newBuilding",
        defaultMessage: "New Building",
      });
    } else if (isReadOnly) {
      return intl.formatMessage({
        id: "button.viewBuilding",
        defaultMessage: "View Building",
      });
    } else {
      return intl.formatMessage({
        id: "button.editBuilding",
        defaultMessage: "Edit Building",
      });
    }
  }

  return undefined;
};

export const getTabLabel = (tabKey: string, ctx: RecordPageRenderContext) => {
  const {
    schema: { title: setupName },
    language,
    t,
    rowDataReducerState: { rowData },
  } = ctx;
  const label = getTitleFromKey(tabKey, t, language);

  if (setupName === SCHEMA_TITLES.INVENTORY_BUILDINGS) {
    if (tabKey === ROW_DATA_KEYS.INVENTORY_ZONES) {
      return `${label} (${(rowData.zones as RowDataObject[])?.length ?? 0})`;
    }

    if (tabKey === ROW_DATA_KEYS.INVENTORY_ROUTES) {
      return `${label} (${(rowData.route_names as RowDataObject[])?.length ?? 0})`;
    }
  }
  return getTitleFromKey(tabKey, t, language);
};

export const getSelectedId = (selected: LandingPageOptions): string => {
  switch (selected.department.value) {
    case DEPARTMENTS.ACCOUNTING:
      return selected.groupId.label;
    case DEPARTMENTS.SERVICE:
      return selected.departmentId.label;
    case DEPARTMENTS.PARTS:
      return selected.departmentId.label;
    default:
      return selected.store.label;
  }
};

export const getExportSchemaTitles = (
  schema: JsonSchema,
): Record<string, string> => {
  return schema.title === SCHEMA_TITLES.CHART_OF_ACCOUNTS
    ? getReadOnlyMarkedSchemaTitles(schema)
    : getSchemaTitles(schema);
};

export const getReadOnlyMarkedSchemaTitles = (
  schema: JsonSchema,
): Record<string, string> => {
  return Object.fromEntries(
    Object.entries(getSchemaTitles(schema)).map(([key, title]) => [
      key,
      COA_EXPORT_READ_ONLY_FIELDS.has(key) ? `${title}_READ_ONLY` : title,
    ]),
  );
};
